<template>
  <div>
    <div class="primary-grey--text text-body-2 font-weight-bold mb-4">
      Want to keep asking questions?
    </div>

    <div
      class="primary-grey--text text-body-2"
      :class="{ 'mb-4': !supportsToggle }"
    >
      To access Sinoa and purchase additional questions,
      you’ll first need to upgrade to a paid GrainFox plan (this is your base plan)<br>
      Choose your plan below.
    </div>

    <template v-if="supportsToggle">
      <div class="d-flex align-center">
        <span
          :class="billingCycle === 'monthly' ? 'primary--text' : 'grey--text'"
          class="mr-3 cursor-pointer text-body-1"
          @click="billingCycle = 'monthly'"
        >
          Billed Monthly
        </span>
        <v-switch
          v-model="billingCycle"
          :false-value="'monthly'"
          :true-value="'annual'"
          inset
        />
        <span
          :class="billingCycle === 'annual' ? 'primary--text' : 'grey--text'"
          class="ml-3 cursor-pointer text-body-1"
          @click="billingCycle = 'annual'"
        >
          Billed Annually
        </span>
      </div>
      <div class="primary-grey--text text-body-2 font-weight-bold mb-4">
        Save 10% with annual billing
      </div>
    </template>

    <GCarousel>
      <GCarouselItem
        v-for="(plan, index) in displayedPlans"
        :key="index"
      >
        <PlanCard
          v-bind="plan"
          ref="planCards"
          :title="plan.title"
          :subtitle="plan.subtitle"
          :price="plan.price"
          :frequency="billingCycle === 'annual' ? 'year' : 'month'"
          :features="plan.features"
          :buttons="plan.buttons"
          :disabled="plan.disabled"
          :card-clicked="subscriptionCart.selectedPlanIndex === index"
          @card-click="subscriptionCart.selectedPlanIndex = index"
        />
      </GCarouselItem>
    </GCarousel>
  </div>
</template>

<script>
import PlanCard from './PlanCard.vue';
import { mapGetters, mapState } from 'vuex';
import CATALOG from './data/_catalog';
import GCarousel from '../generic/GCarousel.vue';
import GCarouselItem from '../generic/GCarouselItem.vue';

export default {
  name: 'PlanSelection',

  components:
  {
    PlanCard,
    GCarousel,
    GCarouselItem,
  },

  data() {
    return {
      billingCycle: 'annual',
    };
  },

  computed: {
    ...mapGetters('subscription', ['isCanadian']),
    ...mapState('chatbot', ['subscriptionCart']),

    plans() {
      const region = this.isCanadian ? 'CA' : 'US';
      return (CATALOG[region] && CATALOG[region].plans) || [];
    },

    supportsMonthly() {
      return this.plans.length > 0
        && this.plans.every((p) => typeof p.priceMonthly === 'number' && p.priceMonthly !== null);
    },

    supportsAnnual() {
      return this.plans.length > 0
        && this.plans.every((p) => typeof p.priceAnnual === 'number' && p.priceAnnual !== null);
    },

    supportsToggle() {
      return this.supportsMonthly && this.supportsAnnual;
    },

    displayedPlans() {
      const useAnnual = !this.supportsToggle || this.billingCycle === 'annual';
      const currency = this.isCanadian ? 'CAD' : 'USD';
      const freq = useAnnual ? 'Yearly' : 'Monthly';

      return this.plans.map((p) => {
        const isBasic = String(p.title || '').toLowerCase() === 'basic';
        const priceId = `${p.id}-${currency}-${freq}`;
        return {
          ...p,
          price: useAnnual ? p.priceAnnual : p.priceMonthly,
          disabled: isBasic,
          priceId,
          buttons: [
            {
              buttonText: isBasic ? 'Current Plan' : 'Select',
              action: () => {
                if (!isBasic) {
                  this.$emit('plan-select', { ...p, id: priceId }, this.billingCycle);
                }
              },
            },
          ],
        };
      });
    },
  },

  mounted() {
    this.scrollToSelected(this.subscriptionCart.selectedPlanIndex);
  },

  methods: {
    scrollToSelected(index = this.subscriptionCart.selectedPlanIndex) {
      this.$nextTick(() => {
        const cards = this.$refs.planCards;
        if (cards && cards[index]) {
          const el = cards[index].$el || cards[index];
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    },
  },
};
</script>

<style scoped>
@media (max-width: 600px) {
  ::v-deep .g-carousel {
    max-width: 100vw;
    overflow: hidden;
  }

  ::v-deep .v-card,
  ::v-deep .v-card[width],
  ::v-deep .v-card[width="280px"] {
    width: 240px !important;
    min-width: 240px !important;
    max-width: 240px !important;
  }

  ::v-deep .g-carousel__items {
    max-width: calc(100vw - 48px);
  }
}
</style>
